import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const WIDTH = 1080;
const HEIGHT = 1080;
const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicPreviewRivalry({
  graphicData,
  teamColors,
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  detailTextColor = null,
  teamSport = 'Baseball',
  // dominantImage intentionally unused — layout is logo-centric
  dateFormat = 'full',
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;
  const primary = teamColors.primary;
  const secondary = teamColors.secondary;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlow = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const displayHeroFontSize = heroFontSize != null ? Number(heroFontSize) : 90;

  return (
    <div
      id="graphic-preview"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1 — Left half, primary color */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 540,
          height: HEIGHT,
          background: primary,
          zIndex: 1,
        }}
      />

      {/* Layer 2 — Right half, secondary color */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 540,
          width: 540,
          height: HEIGHT,
          background: secondary,
          zIndex: 1,
        }}
      />

      {/* Layer 3 — Center diagonal seam */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          left: 500,
          width: 80,
          height: HEIGHT + 400,
          background: 'rgba(0,0,0,0.3)',
          transform: 'rotate(10deg)',
          zIndex: 3,
        }}
      />

      {/* Layer 4 — "GAME DAY" header, centered full width */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          width: WIDTH,
          textAlign: 'center',
          fontFamily: heroFontFamily,
          fontSize: displayHeroFontSize,
          lineHeight: 1,
          color: '#ffffff',
          letterSpacing: 10,
          textShadow: '0 4px 24px rgba(0,0,0,0.7)',
          zIndex: 4,
        }}
      >
        GAME DAY
      </div>

      {/* Layer 5 — Home logo centered in left half, or sport emoji fallback */}
      {homeTeamLogo ? (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 280,
            left: 270,
            transform: 'translateX(-50%)',
            height: 280,
            width: 'auto',
            maxWidth: 320,
            zIndex: 5,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 280,
            left: 0,
            width: 540,
            textAlign: 'center',
            fontSize: 160,
            lineHeight: 1,
            zIndex: 5,
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}

      {/* Layer 6 — Away logo centered in right half, or sport emoji fallback */}
      {awayTeamLogoPreview ? (
        <img
          src={awayTeamLogoPreview}
          alt={awayTeam}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 280,
            left: 810,
            transform: 'translateX(-50%)',
            height: 280,
            width: 'auto',
            maxWidth: 320,
            zIndex: 6,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 280,
            left: 540,
            width: 540,
            textAlign: 'center',
            fontSize: 160,
            lineHeight: 1,
            zIndex: 6,
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}

      {/* Layer 7 — "VS" centered full width */}
      <div
        style={{
          position: 'absolute',
          top: 556,
          left: 0,
          width: WIDTH,
          textAlign: 'center',
          fontFamily: heroFontFamily,
          fontSize: 180,
          lineHeight: 1,
          color: '#ffffff',
          letterSpacing: 6,
          textShadow: '0 4px 40px rgba(0,0,0,0.8)',
          zIndex: 7,
        }}
      >
        VS
      </div>

      {/* Layer 8 — Home team name centered in left half */}
      <div
        style={{
          position: 'absolute',
          top: 720,
          left: 0,
          width: 510,
          textAlign: 'center',
          fontFamily: heroFontFamily,
          fontSize: 56,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          letterSpacing: 2,
          textShadow: '0 2px 16px rgba(0,0,0,0.7)',
          zIndex: 8,
        }}
      >
        {homeTeam || 'YOUR TEAM'}
      </div>

      {/* Layer 9 — Away team name centered in right half */}
      <div
        style={{
          position: 'absolute',
          top: 720,
          left: 570,
          width: 510,
          textAlign: 'center',
          fontFamily: heroFontFamily,
          fontSize: 56,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          letterSpacing: 2,
          textShadow: '0 2px 16px rgba(0,0,0,0.7)',
          zIndex: 9,
        }}
      >
        {awayTeam || 'OPPONENT'}
      </div>

      {/* Layer 10 — Date / time / location, centered full width */}
      <div
        style={{
          position: 'absolute',
          top: 840,
          left: 0,
          width: WIDTH,
          textAlign: 'center',
          fontFamily: barlow,
          fontSize: 36,
          fontWeight: 600,
          lineHeight: 1.5,
          color: detailTextColor || 'rgba(255,255,255,0.85)',
          textShadow: '0 2px 12px rgba(0,0,0,0.6)',
          zIndex: 10,
        }}
      >
        {formatGameDate(gameDate, dateFormat) && <div>{formatGameDate(gameDate, dateFormat)}</div>}
        {gameTime && <div>{formatGameTime(gameTime)}</div>}
        {location && <div>{location}</div>}
      </div>

      {/* Layer 11 — Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 11,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicPreviewRivalry;
